import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRightIcon, ArrowDownRightIcon } from "@heroicons/react/20/solid";
import { useVenue } from "../VenueProvider";
import { ACCENT, MONTHS_ES, MONTHS_SHORT, defaultMonth } from "../cuentas/shared";
import { getMonthRange, getYearRange } from "../../utils/dates";
import { serieMensual } from "../../utils/reportes";

// ── Períodos ───────────────────────────────────────────────────────────
// Cada preset trae su rango y el rango ANTERIOR equivalente: los KPIs
// siempre se leen contra algo, no en el vacío. "custom" es un mes concreto
// elegido con el selector de mes.
const mk = (y, m0) => `${y}-${String(m0 + 1).padStart(2, "0")}`;

export const PERIODOS = [
  { id: "mes", label: "Este mes" },
  { id: "mes_pasado", label: "Mes pasado" },
  { id: "anio", label: "Este año" },
  { id: "12m", label: "Últimos 12 meses" },
];

// Ancla UTC <-> "YYYY-MM-DD" de <input type="date">. Los rangos son anclas
// UTC del día calendario, así que se leen y arman con componentes UTC.
const isoDeAncla = (f) =>
  `${f.getUTCFullYear()}-${String(f.getUTCMonth() + 1).padStart(2, "0")}-${String(f.getUTCDate()).padStart(2, "0")}`;
const ISO_DIA = /^\d{4}-\d{2}-\d{2}$/;
const inicioDia = (iso) => {
  const [yy, mm, dd] = iso.split("-").map(Number);
  return new Date(Date.UTC(yy, mm - 1, dd, 0, 0, 0, 0));
};
const finDia = (iso) => {
  const [yy, mm, dd] = iso.split("-").map(Number);
  return new Date(Date.UTC(yy, mm - 1, dd, 23, 59, 59, 999));
};
const fechaCorta = (f) =>
  `${f.getUTCDate()} ${MONTHS_SHORT[f.getUTCMonth()].toLowerCase()} ${f.getUTCFullYear()}`;
const DIA_MS = 86400000;

// Las fechas de un rango ya armado, para sembrar el selector de rango con
// lo que se estaba viendo.
export const rangoAIso = (rango) =>
  rango ? { desde: isoDeAncla(rango.from), hasta: isoDeAncla(rango.to) } : null;

export function rangoPeriodo(periodo, mes, ahora = new Date(), libre = null) {
  const y = ahora.getFullYear();
  const m = ahora.getMonth();
  // Rango libre: se compara contra el mismo número de días justo antes. Si
  // las fechas vienen al revés (URL editada a mano) se acomodan.
  if (periodo === "rango" && ISO_DIA.test(libre?.desde || "") && ISO_DIA.test(libre?.hasta || "")) {
    const [a, b] = libre.desde <= libre.hasta ? [libre.desde, libre.hasta] : [libre.hasta, libre.desde];
    const from = inicioDia(a);
    const to = finDia(b);
    // Un mes calendario completo se lee como mes: vs el mes anterior, no
    // vs "los 30 días previos". Es lo que hacía el selector de mes que este
    // rango reemplazó.
    const mesDe = a.slice(0, 7);
    if (a.endsWith("-01") && b.slice(0, 7) === mesDe && to.getTime() === getMonthRange(mesDe).to.getTime()) {
      return rangoPeriodo("custom", mesDe, ahora);
    }
    const dias = Math.round((to - from) / DIA_MS);
    const prevTo = new Date(from.getTime() - 1);
    const prevFrom = new Date(from.getTime() - dias * DIA_MS);
    return {
      from,
      to,
      prev: { from: prevFrom, to: prevTo },
      etiqueta: a === b ? fechaCorta(from) : `${fechaCorta(from)} al ${fechaCorta(to)}`,
      comparado: dias === 1 ? "vs el día anterior" : `vs los ${dias} días previos`,
    };
  }
  if (periodo === "custom" && mes) {
    const [my, mm] = mes.split("-").map(Number);
    const prevD = new Date(my, mm - 2, 1);
    return {
      ...getMonthRange(mes),
      prev: getMonthRange(mk(prevD.getFullYear(), prevD.getMonth())),
      etiqueta: `${MONTHS_ES[mm - 1]} ${my}`,
      comparado: "vs el mes anterior",
    };
  }
  if (periodo === "mes_pasado") {
    const prev = new Date(y, m - 1, 1);
    const prev2 = new Date(y, m - 2, 1);
    return {
      ...getMonthRange(mk(prev.getFullYear(), prev.getMonth())),
      prev: getMonthRange(mk(prev2.getFullYear(), prev2.getMonth())),
      etiqueta: `${MONTHS_ES[prev.getMonth()]} ${prev.getFullYear()}`,
      comparado: "vs el mes anterior",
    };
  }
  if (periodo === "anio") {
    return {
      ...getYearRange(y),
      prev: getYearRange(y - 1),
      etiqueta: String(y),
      comparado: `vs ${y - 1}`,
    };
  }
  if (periodo === "12m") {
    // Ventana móvil que termina HOY, no en el fin del mes: con meses
    // completos, el 19 de octubre ya contaba los eventos del 20 al 31 como
    // "realizados". Va del día siguiente a hoy hace un año hasta hoy, en
    // las mismas anclas UTC que getMonthRange.
    const d = ahora.getDate();
    const dia = (yy, dd, fin = false) =>
      fin
        ? new Date(Date.UTC(yy, m, dd, 23, 59, 59, 999))
        : new Date(Date.UTC(yy, m, dd, 0, 0, 0, 0));
    const from = dia(y - 1, d + 1);
    const to = dia(y, d, true);
    return {
      from,
      to,
      prev: { from: dia(y - 2, d + 1), to: dia(y - 1, d, true) },
      etiqueta: `Últimos 12 meses · ${fechaCorta(from)} al ${fechaCorta(to)}`,
      comparado: "vs los 12 meses previos",
    };
  }
  // mes actual
  const prev = new Date(y, m - 1, 1);
  return {
    ...getMonthRange(mk(y, m)),
    prev: getMonthRange(mk(prev.getFullYear(), prev.getMonth())),
    etiqueta: `${MONTHS_ES[m]} ${y}`,
    comparado: "vs el mes anterior",
  };
}

// Puente entre las DOS convenciones de fecha del proyecto: los rangos de
// período son anclas UTC (utils/dates.js, mediodía UTC del día calendario)
// y utils/ocupacion.js razona en días LOCALES. Pasarle un ancla UTC directo
// corre el mes un día en los dos extremos — en CDMX, el 1 de junio a las
// 00:00 UTC es todavía 31 de mayo. `mas` sirve para el fin exclusivo.
export const diaLocalDeAncla = (ancla, mas = 0) =>
  new Date(ancla.getUTCFullYear(), ancla.getUTCMonth(), ancla.getUTCDate() + mas);

// "hace cuánto" en días de CALENDARIO, no en bloques de 24 horas: algo de
// ayer a las 9 pm debe leerse "ayer" aunque hayan pasado 16 horas. Con el
// cálculo por milisegundos los dos reportes de uso se contradecían.
export const haceEnDias = (fecha) => {
  if (!fecha) return "";
  const a = new Date(fecha);
  const b = new Date();
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  const dias = Math.round((b - a) / 86400000);
  if (dias <= 0) return "hoy";
  if (dias === 1) return "ayer";
  return `hace ${dias} días`;
};

export const ultimosMeses = (n = 12, ahora = new Date()) => {
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
    out.push(mk(d.getFullYear(), d.getMonth()));
  }
  return out;
};

export const mesesDeAnio = (anio) =>
  Array.from({ length: 12 }, (_, i) => mk(anio, i));

export const labelMes = (key) => {
  const [yy, mm] = key.split("-").map(Number);
  return `${MONTHS_SHORT[mm - 1]} ${String(yy).slice(2)}`;
};

// El período vive en la URL para que un reporte acotado se pueda compartir
// o dejar en un bookmark tal cual se está viendo.
// `?periodo=custom&mes=` ya no lo escribe el selector (lo reemplazó el
// rango de fechas), pero se sigue leyendo para no romper enlaces viejos.
export function usePeriodo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const periodo = searchParams.get("periodo") || "mes";
  const mes = searchParams.get("mes") || defaultMonth();
  const desde = searchParams.get("desde") || "";
  const hasta = searchParams.get("hasta") || "";
  const rango = useMemo(
    () => rangoPeriodo(periodo, mes, new Date(), { desde, hasta }),
    [periodo, mes, desde, hasta]
  );
  const setPeriodo = (id) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("periodo", id);
      return next;
    }, { replace: true });
  // Cualquiera de los dos extremos; el otro se conserva.
  const setRango = (valores) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("periodo", "rango");
      Object.entries(valores).forEach(([k, v]) => v && next.set(k, v));
      return next;
    }, { replace: true });
  return { periodo, rango, setPeriodo, setRango };
}

// Control segmentado de período. La etiqueta del rango vive en la cabecera
// del reporte (ReporteDetalle); aquí solo se repite en móvil, donde la
// cabecera no la muestra.
const inputFecha = (activo) =>
  `rounded-lg border px-2.5 py-1.5 text-sm bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 ${
    activo ? "font-semibold" : "border-zinc-200 dark:border-zinc-700"
  }`;

// Las fechas se leen del rango ya resuelto, no de la URL: así reflejan
// también el preset activo y un enlace viejo de mes concreto
// (?periodo=custom&mes=), y cambiar un extremo siempre manda los dos.
export const PeriodoSelector = ({ periodo, setPeriodo, setRango, rango }) => {
  const fechas = rangoAIso(rango);
  const esRango = periodo === "rango" || periodo === "custom";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 overflow-hidden divide-x divide-zinc-200 dark:divide-zinc-700 shadow-sm">
        {PERIODOS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPeriodo(p.id)}
            className={`px-3 py-1.5 text-sm font-semibold transition whitespace-nowrap ${
              periodo === p.id
                ? "text-white"
                : "text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700/50"
            }`}
            style={periodo === p.id ? { backgroundColor: ACCENT } : undefined}
          >
            {p.label}
          </button>
        ))}
      </div>
      {/* Rango libre, siempre a la vista: las fechas son las del período que
          se está viendo, y cambiar cualquiera lo vuelve rango. Con un preset
          activo se ven en gris, como lectura; en verde cuando mandan ellas. */}
      <div className="inline-flex items-center gap-1.5 text-sm text-zinc-500">
        <input
          type="date"
          value={fechas?.desde || ""}
          max={fechas?.hasta}
          onChange={(e) => e.target.value && setRango({ ...fechas, desde: e.target.value })}
          className={inputFecha(esRango)}
          style={esRango ? { borderColor: ACCENT, color: ACCENT } : undefined}
          aria-label="Desde"
        />
        <span>al</span>
        <input
          type="date"
          value={fechas?.hasta || ""}
          min={fechas?.desde}
          onChange={(e) => e.target.value && setRango({ ...fechas, hasta: e.target.value })}
          className={inputFecha(esRango)}
          style={esRango ? { borderColor: ACCENT, color: ACCENT } : undefined}
          aria-label="Hasta"
        />
      </div>
      {rango && (
        <span className="ml-auto text-xs text-zinc-400 sm:hidden">
          {rango.etiqueta} · variaciones {rango.comparado}
        </span>
      )}
    </div>
  );
};

// ── Serie anual compartida ─────────────────────────────────────────────
// El Estado de resultados (devengado) y el Flujo de caja navegan el mismo
// año (?anio= en la URL) sobre la misma serie mensual; cada reporte lee su
// mitad de las columnas.
// Los depósitos de las dos fuentes, mezclados. Los de proveedor no viven
// suscritos: snapshot al abrir la vista, igual que en Cuentas.
// Los depósitos de proveedor no viven en el contexto: se cargan con una
// lectura por proveedor. ReportesPage los carga una vez y se los pasa a cada
// reporte por prop; con `enabled: false` el hook no vuelve a leer nada.
export function useDepositos(enabled = true) {
  const { clienteDepositos, loadProveedorDepositos } = useVenue();
  const [provDepositos, setProvDepositos] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let vivo = true;
    loadProveedorDepositos()
      .then((deps) => { if (vivo) setProvDepositos(deps); })
      .catch(() => { if (vivo) setProvDepositos([]); });
    return () => { vivo = false; };
  }, [loadProveedorDepositos, enabled]);
  return useMemo(
    () => (enabled ? [...(clienteDepositos || []), ...provDepositos] : []),
    [enabled, clienteDepositos, provDepositos]
  );
}

// `depositosProp`: los que ReportesPage ya cargó. Sin prop (un reporte
// montado fuera de ReportesPage) el hook los carga él mismo.
export function useSerieAnual(depositosProp = null) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    events,
    payments,
    refunds,
    costos,
    retiros,
    contactForms,
    calendarTimezone,
  } = useVenue();
  // Entran al Flujo de caja (FRA-346).
  const depositosCargados = useDepositos(!depositosProp);
  const depositos = depositosProp || depositosCargados;
  const currentYear = new Date().getFullYear();
  const anio = Number(searchParams.get("anio")) || currentYear;
  const years = [];
  for (let y = 2022; y <= currentYear + 1; y++) years.push(y);
  const setAnio = (y) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("anio", String(y));
      return next;
    }, { replace: true });
  const serie = useMemo(
    () =>
      serieMensual({
        meses: mesesDeAnio(anio),
        events,
        payments,
        refunds,
        costos,
        depositos,
        retiros,
        contactForms,
        timeZone: calendarTimezone,
        getMonthRange,
      }).map((m, i) => ({
        ...m,
        label: MONTHS_ES[i],
        // serieMensual entrega la utilidad; el costo de los eventos del mes
        // es su complemento contra el valor.
        costosEventos: m.valor - m.utilidad,
        futuro: getMonthRange(m.key).from > new Date(),
      })),
    [anio, events, payments, refunds, costos, depositos, retiros, contactForms, calendarTimezone]
  );
  return {
    anio,
    years,
    setAnio,
    serie,
    tz: calendarTimezone,
    events,
    payments,
    refunds,
    costos,
    depositos,
    retiros,
  };
}

export const SelectorAnio = ({ anio, years, setAnio }) => (
  <div className="flex items-center gap-2">
    <span className="text-sm text-zinc-500">Año</span>
    <select
      value={anio}
      onChange={(e) => setAnio(Number(e.target.value))}
      className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-1.5 text-sm"
    >
      {years.map((y) => (
        <option key={y} value={y}>{y}</option>
      ))}
    </select>
  </div>
);

// ── Piezas de UI ───────────────────────────────────────────────────────

// Chip de variación vs. el período anterior. null = sin base ("—" sería
// ruido; simplemente no se pinta). `invertir` es para métricas donde subir
// es malo (costos).
export const Delta = ({ valor, invertir = false, sufijo = "" }) => {
  if (valor === null || valor === undefined || !isFinite(valor)) return null;
  const positivoEsBueno = invertir ? valor < 0 : valor >= 0;
  const Icon = valor >= 0 ? ArrowUpRightIcon : ArrowDownRightIcon;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
        positivoEsBueno ? "text-emerald-600" : "text-red-600"
      }`}
    >
      <Icon className="h-3 w-3" />
      {Math.abs(valor * 100).toFixed(0)}%{sufijo}
    </span>
  );
};

export const Kpi = ({ label, value, delta, invertir, hint, tone = "" }) => (
  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm">
    <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
      {label}
    </div>
    <div className="mt-1 flex items-baseline gap-2 flex-wrap">
      <span className={`text-2xl font-bold ${tone || "text-zinc-900 dark:text-white"}`}>
        {value}
      </span>
      <Delta valor={delta} invertir={invertir} />
    </div>
    {hint && <div className="mt-0.5 text-xs text-zinc-500">{hint}</div>}
  </div>
);

// Eje de dinero en corto. El resto de los reportes usa `$Nk` fijo, que con
// un venue de eventos grandes escupe "$2257k"; arriba del millón esto pasa a
// "$2.3M". El número completo siempre está en el tooltip y en la tabla.
export const fmtEje = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1000) return `$${(n / 1000).toFixed(0)}k`;
  return `$${n}`;
};

export const fmtPct = (v) =>
  v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`;

// Los costos son una serie, no una alarma: el rojo decía "algo está mal" en
// cada gráfica de egresos. Cian, validado (scripts del skill dataviz, modo
// claro) contra todo lo que comparte gráfica con él: el verde de marca
// (Ingresos/Utilidad), el violeta y el ámbar de Estructura de costos, el
// ámbar de Planners y el gris de Proveedores. El rojo queda reservado para
// lo que sí es malo: cartera vencida, utilidad negativa.
export const COSTO_COLOR = "#0891B2";
export const VALOR_COLOR = "#3B82F6";
export const ALERTA_COLOR = "#DC2626";

// Agenda vendida: cobrado y saldo NO son dos categorías, son el mismo dinero
// contratado en dos estados, así que van en una rampa de un solo tono (verde
// de marca) y no en dos colores que compitan. Validados contra la superficie
// clara: monotonía de luminosidad, separación de paso y contraste del extremo
// claro. El panel no enciende el modo oscuro (tailwind `darkMode: 'class'` y
// nadie pone la clase), por eso hay una sola rampa y no dos.
export const AGENDA_COBRADO = "#18633C";
export const AGENDA_SALDO = "#46A876";
